import React, { useEffect, useRef, useState } from "react";
import { FormResolveType, updateStandardFormErrorMessage } from "../../redux/AppStateSlice";
import { Button, ButtonSize, ButtonVariant, FormInput, MITheme } from "@spectrumselect/mi-ui-react";
import { Journey } from "../../redux";
import { ErrorMessage } from "../../components/errorMessage/ErrorMessage";
import PassInput from "../../components/shared/PasswordInput/PasswordInput"
import {
  hasConsecutiveChar,
  hasLowerCase,
  hasNumber,
  hasSeqChar,
  hasSpChar,
  hasUpperCase,
  hasNotAllowedSpChar
} from "../../utils/formValidation";
import "./CollectPasswordForm.scss";
import { useDispatch } from "react-redux";
import { local } from "../../internationalization/Localization";
import { safeEqual } from "../../utils/urlUtils";
import { S } from "../../auth/sdk"
import Tick from "../../images/tick.svg";

interface CollectPasswordFormProps {
  resolve?: FormResolveType;
  initiatingPolicyId: Journey;
  errorMessage?: string;
  tenant?: string;
  isActivation?: boolean;
}

export const CollectPasswordForm: React.FunctionComponent<CollectPasswordFormProps> = (props) => {

  //--- states ---//
  const [password, setPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  //--- refs ---//
  const firstInputRef = useRef<HTMLInputElement>(null);
  //--- hooks ---//
  const dispatch = useDispatch()
  useEffect(() => {
    if (props.errorMessage) {
      setPassword(""); //clear inputs
      setConfirmPassword("");
      firstInputRef.current?.focus();
    }
  }, [props.errorMessage])

  //--- utility methods ---//
  const isPasswordValid = (pass: string): boolean => {
    const hasLength = pass.length >= 8 && pass.length <= 127
    const has3of4Rules = (): boolean => {
      let count = 0
      if (hasLowerCase(pass)) count++
      if (hasUpperCase(pass)) count++
      if (hasNumber(pass)) count++
      if (hasSpChar(pass, "@#%$^&+=", 1)) count++ //has allowed special character
      if (hasNotAllowedSpChar(pass, '@#%$^&+=', 1)) count = 0 //has not allowed special character
      return count >= 3
    }
    const hasNoSeqPatterns = !hasSeqChar(pass, 4)
    const hasNoConsecutiveCharacters = !hasConsecutiveChar(pass, 4)
    return hasLength && has3of4Rules() && hasNoSeqPatterns && hasNoConsecutiveCharacters
  }

  //--- callback methods ---//

  const onSubmitButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    dispatch(updateStandardFormErrorMessage("")) //clear errorMessage
    //const S = window.com.ts.mobile.sdk
    const paramData = props.initiatingPolicyId === Journey.CHANGE_PASSWORD ? { current_password: currentPassword, password: password, confirm_password: confirmPassword } : { password: password, confirm_password: confirmPassword }
    props.resolve && props.resolve(S.FormInput.createFormInputSubmissionRequest({
      actions: paramData
    }));
  }

  //--- ui setup ---//

  const rules = local.getProp("collectPasswordForm.rules.list");

  // const pageTitle =
  //   props.initiatingPolicyId === Journey.FORGOT_PASSWORD ? local.get('collectPasswordForm.title.forgotPassword')
  //     : props.initiatingPolicyId === Journey.CHANGE_PASSWORD ? local.get('collectPasswordForm.title.changePassword')
  //       : props.initiatingPolicyId === Journey.COMPLETE_USER_REGISTRATION ? local.get('collectPasswordForm.title.userRegistration')
  //         : local.get('collectPasswordForm.title.default');

  // const pageDescription =
  //   props.initiatingPolicyId === Journey.FORGOT_PASSWORD ? local.get('collectPasswordForm.description.forgotPassword')
  //     : props.initiatingPolicyId === Journey.CHANGE_PASSWORD ? local.get('collectPasswordForm.description.changePassword')
  //       : props.initiatingPolicyId === Journey.COMPLETE_USER_REGISTRATION ? local.get('collectPasswordForm.description.userRegistration')
  //         : local.get('collectPasswordForm.description.default');

  const enterPasswordInputLabel =
    props.initiatingPolicyId === Journey.FORGOT_PASSWORD ? local.get("collectPasswordForm.enterPasswordLabel.forgotPassword")
      : props.initiatingPolicyId === Journey.CHANGE_PASSWORD ? local.get("collectPasswordForm.enterPasswordLabel.changePassword")
        : props.initiatingPolicyId === Journey.COMPLETE_USER_REGISTRATION ? local.get("collectPasswordForm.enterPasswordLabel.userRegistration")
          : local.get("collectPasswordForm.enterPasswordLabel.default")

  const enterCurrentPasswordInputLabel =
    props.initiatingPolicyId === Journey.CHANGE_PASSWORD ? local.get("collectPasswordForm.currentEnterPasswordLabel.changePassword")
      : local.get("collectPasswordForm.enterPasswordLabel.default")


  const reEnterPasswordInputLabel =
    props.initiatingPolicyId === Journey.FORGOT_PASSWORD ? local.get("collectPasswordForm.reEnterPasswordLabel.forgotPassword")
      : props.initiatingPolicyId === Journey.CHANGE_PASSWORD ? local.get("collectPasswordForm.reEnterPasswordLabel.changePassword")
        : props.initiatingPolicyId === Journey.COMPLETE_USER_REGISTRATION ? local.get("collectPasswordForm.reEnterPasswordLabel.userRegistration")
          : local.get("collectPasswordForm.reEnterPasswordLabel.default")

  const submitButtonText = props.initiatingPolicyId === Journey.COMPLETE_USER_REGISTRATION ? local.get("buttonLabels[1]") : local.get("buttonLabels[2]");
  const handleTick = (val, subVal?) => {
    if (val === 0) {
     return  password.length >= 8 && password.length <= 127
    }
    if (val === 1 ){
      if (subVal === undefined){
        return [hasLowerCase(password), hasLowerCase(password), hasUpperCase(password),hasSpChar(password, "#@!$%^&*()_+~/|", 1)].filter(Boolean).length >= 3
      }
      if (subVal === 0){
        return hasLowerCase(password)
      }
      if (subVal === 1){
        return hasUpperCase(password)
      }
      if (subVal === 2){
        return hasNumber(password)
      }
      if (subVal === 3){
        return (hasSpChar(password, "@#%$^&+=", 1) && !hasNotAllowedSpChar(password, '@#%$^&+=', 1))
      }
    }
    if (val === 2) {
      return  password.length && (!hasConsecutiveChar(password, 4) &&  !hasSeqChar(password, 4))
     }
  }
  return (
    <form id="collect_current_and_new_password" className="CollectPasswordForm">
      <h1 className="App_pageTitle">{props.isActivation || props.initiatingPolicyId === Journey.COMPLETE_USER_REGISTRATION ? "Create" : "Update"} your password</h1>
      {/* <p className="App_formDesc">{pageDescription}</p> */}
      {!!props.errorMessage && <ErrorMessage>{props.errorMessage}</ErrorMessage>}
      <div className="CollectPasswordForm_Container">
        <FormInput
          label={"Username"}
          defaultValue={"user"}
          autoComplete={"username"}
          hidden
          theme={MITheme.dark}
        />
        {props.initiatingPolicyId === Journey.CHANGE_PASSWORD && <PassInput
          className={"mt-md-2"}
          ref={firstInputRef}
          placeholder={enterCurrentPasswordInputLabel}
          label=""
         // error={!isPasswordValid(currentPassword) && currentPassword.length > 0 ? local.get('collectPasswordForm.errors[0]') : ''}
          value={currentPassword}
          autoComplete={"current-password"}
          // theme={MITheme.dark}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setCurrentPassword(event.target.value);
          }}
        />}
        <PassInput
          className={"mt-md-2"}
          ref={firstInputRef}
          placeholder={enterPasswordInputLabel}
          label=""
          error={!isPasswordValid(password) && password.length > 0 ? local.get("collectPasswordForm.errors[0]") : ""}
          value={password}
          autoComplete={"new-password"}
          // theme={MITheme.dark}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setPassword(event.target.value);
          }}
        />
        <PassInput
          placeholder={reEnterPasswordInputLabel}
          label=""
          error={!safeEqual(password, confirmPassword) && confirmPassword.length !== 0 && isPasswordValid(password) ? local.get("collectPasswordForm.errors[1]") : ""}
          value={confirmPassword}
          autoComplete={"new-password"}
          // theme={MITheme.dark}
          onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
            setConfirmPassword(event.target.value);
          }}
        />
      </div>
      <div className={"App_formButtonGroup CollectPasswordForm_ButtonGroup"}>
        <Button
          className={`login100-form-btn ${props.tenant}-btn`}
          variant={ButtonVariant.primary}
          disabled={!(isPasswordValid(password) && isPasswordValid(confirmPassword) && safeEqual(password, confirmPassword))}
          type="submit"
          onClick={onSubmitButtonClick}
          theme={MITheme.dark}
          size={ButtonSize.large}
        >
          {submitButtonText}
        </Button>
      </div>
      {rules.length > 0 && (
        <div className="CollectPasswordForm_RuleContainer">
          <span>{local.get("collectPasswordForm.rules.title")}</span>
          <ul className="CollectPasswordForm_RuleListOuter">
            {rules.map((rule: string | string[], index: number) =>
              typeof rule === "string" ? (
                !(index === 3 && (props.isActivation || props.initiatingPolicyId === Journey.COMPLETE_USER_REGISTRATION)) && <li className={`${index === 2 && "break-section"}`} key={rule}>{handleTick(index) ? <img className='password-tick' src={Tick} alt="tick" /> : null}&nbsp;{rule}</li>
              ) : Array.isArray(rule) && rule.length > 0 ? (
                <li key={rule[0]}>
                  {handleTick(index) ? <img className='password-tick' src={Tick} alt="tick" /> : null}&nbsp;{rule[0]}
                  {rule.length > 1 && (
                    <ul className="CollectPasswordForm_RuleListNested">
                      {rule
                        .filter((subRule: string, i) => i !== 0)
                        .map((subRule: string, ind: number) => (
                          <li key={subRule}>{handleTick(index, ind) ? <img className='password-tick' src={Tick} alt="tick" /> : null}&nbsp;{subRule}</li>
                        ))}
                    </ul>
                  )}
                </li>
              ) : (
                <></>
              )
            )}
          </ul>
        </div>
      )}
    </form>
  );
}
